import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { JujuModal } from "@/components/JujuModal";

const FACE_ROT = {
  1: { x: 0, y: 0 },
  2: { x: -90, y: 0 },
  3: { x: 0, y: -90 },
  4: { x: 0, y: 90 },
  5: { x: 90, y: 0 },
  6: { x: 0, y: 180 },
};

const PIPS = {
  1: [4],
  2: [0, 8],
  3: [0, 4, 8],
  4: [0, 2, 6, 8],
  5: [0, 2, 4, 6, 8],
  6: [0, 2, 3, 5, 6, 8],
};

const FACE_TRANSFORMS = [
  "rotateY(0deg) translateZ(34px)",
  "rotateY(180deg) translateZ(34px)",
  "rotateY(90deg) translateZ(34px)",
  "rotateY(-90deg) translateZ(34px)",
  "rotateX(90deg) translateZ(34px)",
  "rotateX(-90deg) translateZ(34px)",
];

// faces rendered: 1 front, 6 back, 3 right, 4 left, 2 top, 5 bottom
const FACE_VALUES = [1, 6, 3, 4, 2, 5];

const Face = ({ value, transform }) => (
  <div className="die-face" style={{ transform }}>
    {Array.from({ length: 9 }).map((_, i) =>
      PIPS[value].includes(i) ? <span key={i} className="pip" /> : <span key={i} />
    )}
  </div>
);

const spinTo = (cur, target) =>
  target + 360 * (Math.ceil((cur - target) / 360) + 2 + Math.floor(Math.random() * 2));

const Die = ({ value, spinTick, testId, onClick }) => {
  const [rot, setRot] = useState({ x: -18, y: 32 });
  useEffect(() => {
    if (!spinTick) return;
    const t = FACE_ROT[value];
    setRot((prev) => ({ x: spinTo(prev.x, t.x), y: spinTo(prev.y, t.y) }));
  }, [spinTick, value]);
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="die-scene die-shadow cursor-pointer rounded-3xl p-1 transition-transform hover:scale-105"
      aria-label="Roll die"
    >
      <motion.div
        className="die-cube"
        animate={{ rotateX: rot.x, rotateY: rot.y }}
        transition={{ duration: 1.5, ease: [0.15, 0.65, 0.2, 1] }}
      >
        {FACE_TRANSFORMS.map((t, i) => (
          <Face key={i} value={FACE_VALUES[i]} transform={t} />
        ))}
      </motion.div>
    </button>
  );
};

export const DiceGame = ({ rollsLeft, consumeRoll, unlocked }) => {
  const [vals, setVals] = useState([1, 2]);
  const [spinTick, setSpinTick] = useState(0);
  const [result, setResult] = useState(null);
  const [showClaim, setShowClaim] = useState(false);
  const [email, setEmail] = useState("");
  const [claimState, setClaimState] = useState({ loading: false, sent: false, code: "", error: "" });
  const rollingRef = useRef(false);
  const timer = useRef(null);

  const roll = useCallback(async () => {
    if (rollingRef.current || rollsLeft <= 0) return;
    rollingRef.current = true;
    const nextLeft = rollsLeft - 1;
    consumeRoll();
    setResult(null);
    try {
      const { data } = await api.post("/dice/roll");
      setVals([data.die1, data.die2]);
      setSpinTick((t) => t + 1);
      timer.current = setTimeout(() => {
        rollingRef.current = false;
        setResult({ ...data, leftAfter: nextLeft });
        if (data.win) {
          setClaimState({ loading: false, sent: false, code: "", error: "" });
          setShowClaim(true);
        }
      }, 1600);
    } catch {
      rollingRef.current = false;
    }
  }, [rollsLeft, consumeRoll]);

  useEffect(() => {
    const handler = (e) => {
      if (e.target.closest("input, select, textarea, a, button, [data-no-roll]")) return;
      roll();
    };
    window.addEventListener("click", handler);
    return () => {
      window.removeEventListener("click", handler);
      clearTimeout(timer.current);
    };
  }, [roll]);

  const claim = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setClaimState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const { data } = await api.post("/dice/claim", { email });
      setClaimState({ loading: false, sent: true, code: data.code, error: "" });
    } catch {
      setClaimState({ loading: false, sent: false, code: "", error: "Email failed to send — try again." });
    }
  };

  return (
    <>
      <div
        data-testid="chrome-dice-container"
        className="relative z-10 mx-auto -mt-6 flex w-fit flex-col items-center gap-1 py-2"
      >
        <p
          data-testid="dice-hint"
          className="font-tech max-w-[92vw] text-center text-[9px] uppercase tracking-[0.2em] text-white/90 sm:text-[10px] sm:tracking-[0.25em]"
        >
          {rollsLeft > 0
            ? `Click anywhere to roll · 7 wins 10% off · ${rollsLeft} ${rollsLeft === 1 ? "roll" : "rolls"} left`
            : unlocked
              ? "No rolls left — share your pre-order below for 3 more"
              : "Make a pre-order to get 3 rolls — land a 7 for 10% off"}
        </p>
        <div className="flex origin-center scale-75 gap-3 sm:scale-100">
          <Die value={vals[0]} spinTick={spinTick} testId="chrome-die-1" onClick={roll} />
          <Die value={vals[1]} spinTick={spinTick} testId="chrome-die-2" onClick={roll} />
        </div>
        <div data-testid="dice-result-display" className="min-h-[18px] text-center">
          {result && (
            <motion.p
              key={`${result.die1}-${result.die2}-${spinTick}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className={`font-tech text-xs ${result.win ? "font-bold text-white" : "text-white/90"}`}
            >
              {result.win
                ? `${result.die1} + ${result.die2} = 7 — LUCKY SEVEN!`
                : result.leftAfter > 0
                  ? `Total: ${result.total} — roll again for 7`
                  : unlocked
                    ? `Total: ${result.total} — no rolls left, share below for 3 more`
                    : `Total: ${result.total} — make a pre-order below to get 3 rolls`}
            </motion.p>
          )}
        </div>
      </div>

      <JujuModal
        open={showClaim}
        onClose={() => setShowClaim(false)}
        title="Lucky 7 — 10% Off"
        message={claimState.sent ? "Code sent — check your inbox ;)" : "You rolled Good Juju"}
        popupTestId="discount-reward-modal"
        messageTestId="discount-modal-message"
      >
        {claimState.sent ? (
          <p data-testid="discount-code-display" className="font-tech mt-6 rounded-xl border border-dashed border-white/60 bg-white/15 px-4 py-3 text-lg tracking-[0.3em] text-white">
            {claimState.code}
          </p>
        ) : (
          <form onSubmit={claim} className="mt-6 space-y-3">
            <input
              data-testid="discount-email-input"
              type="email"
              required
              className="juju-input text-center"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {claimState.error && <p className="text-sm text-rose-600">{claimState.error}</p>}
            <button
              type="submit"
              data-testid="discount-claim-btn"
              disabled={claimState.loading}
              className="btn-chrome w-full px-6 py-3 text-sm disabled:opacity-70"
            >
              {claimState.loading ? "Sending..." : "Email me 10% off"}
            </button>
          </form>
        )}
      </JujuModal>
    </>
  );
};
